import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	useReactTable,
	getCoreRowModel,
	getSortedRowModel,
	flexRender,
} from "@tanstack/react-table";
import { ActionIcon } from "@mantine/core";
import { useSelector, useDispatch } from "react-redux";
import ButtonWithPopoverFeedback from "../ButtonWithPopoverFeedback";
import { IconCopy, IconEye } from "@tabler/icons-react";
import { setTableSort } from "../../actions/tableSortingActions";
import { CELL_BORDER, FullValueModal, SortIcon } from "./DevTableUtils";

const CHAR_LIMIT = 1200;

function CopyCell({ value, label, truncate, onShowFull }) {
	const [hovered, setHovered] = useState(false);
	const textRef = useRef(null);
	const [isOverflowing, setIsOverflowing] = useState(false);

	const isNull = value === null || value === undefined;
	const strVal = isNull
		? "null"
		: typeof value === "object"
			? JSON.stringify(value)
			: String(value);
	const isCharTruncated = strVal.length > CHAR_LIMIT;
	const displayVal = isCharTruncated
		? strVal.slice(0, CHAR_LIMIT) + "…"
		: strVal;

	useEffect(() => {
		if (truncate && textRef.current) {
			setIsOverflowing(
				textRef.current.scrollHeight > textRef.current.clientHeight + 1
			);
		} else {
			setIsOverflowing(false);
		}
	}, [truncate, strVal]);

	const showEye = hovered && (isOverflowing || isCharTruncated) && !isNull;
	const showCopy = hovered && !isNull;

	return (
		<div
			style={{ position: "relative", minHeight: 22 }}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
		>
			<span
				ref={textRef}
				style={{
					display: truncate ? "-webkit-box" : "block",
					WebkitLineClamp: truncate ? 2 : undefined,
					WebkitBoxOrient: truncate ? "vertical" : undefined,
					overflow: truncate ? "hidden" : undefined,
					fontSize: 12,
					fontFamily: "monospace",
					color: isNull ? "#bbb" : "#222",
					fontStyle: isNull ? "italic" : undefined,
					wordBreak: "break-all",
				}}
			>
				{displayVal}
			</span>
			{showEye || showCopy ? (
				<div
					style={{
						position: "absolute",
						right: 0,
						top: "50%",
						transform: "translateY(-50%)",
						background: "white",
						display: "flex",
						gap: 2,
					}}
				>
					{showEye ? (
						<ActionIcon
							size="xs"
							variant="subtle"
							onClick={() => onShowFull({ value: strVal, label })}
							title="Show full value"
						>
							<IconEye size={11} />
						</ActionIcon>
					) : null}
					{showCopy ? (
						<ButtonWithPopoverFeedback
							iconComponent={<IconCopy size={12} />}
							onClick={() =>
								navigator.clipboard.writeText(strVal)
							}
							feedbackMessage="Copied!"
							title="Copy"
						/>
					) : null}
				</div>
			) : null}
		</div>
	);
}

function CustomRendererCell({ value, label, customRenderer, onShowFull }) {
	const [hovered, setHovered] = useState(false);

	const isNull = value === null || value === undefined;
	const strings = !isNull ? customRenderer.getStrings?.(value) : null;
	const strVal = strings?.rawStr ?? (isNull ? "null" : String(value));

	const showEye = hovered && !isNull;
	const showCopy = hovered && !isNull;

	return (
		<div
			style={{ position: "relative", minHeight: 22 }}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
		>
			{customRenderer(value)}
			{showEye || showCopy ? (
				<div
					style={{
						position: "absolute",
						right: 0,
						top: "50%",
						transform: "translateY(-50%)",
						background: "white",
						display: "flex",
						gap: 2,
					}}
				>
					{showEye ? (
						<ActionIcon
							size="xs"
							variant="subtle"
							onClick={() =>
								onShowFull({
									value: strVal,
									friendlyValue: strings?.friendlyStr ?? null,
									friendlyLabel:
										strings?.friendlyLabel ?? null,
									label,
								})
							}
							title="Show full value"
						>
							<IconEye size={11} />
						</ActionIcon>
					) : null}
					{showCopy ? (
						<ButtonWithPopoverFeedback
							iconComponent={<IconCopy size={12} />}
							onClick={() =>
								navigator.clipboard.writeText(strVal)
							}
							feedbackMessage="Copied!"
							title="Copy"
						/>
					) : null}
				</div>
			) : null}
		</div>
	);
}

function DevDataTable({
	data,
	tableUniqueID,
	columnOrder,
	cellRenderers,
	truncate = true,
}) {
	const dispatch = useDispatch();
	const persistedSort = useSelector((state) =>
		tableUniqueID ? state.tableSorting[tableUniqueID]?.sort : null
	);

	const [sorting, setSorting] = useState(
		() => persistedSort?.map((s) => ({ id: s.id, desc: s.desc })) ?? []
	);
	const [fullValue, setFullValue] = useState(null);

	const columns = useMemo(() => {
		if (!data || data.length === 0) return [];
		const allKeys = [...new Set(data.flatMap((row) => Object.keys(row)))];
		let orderedKeys;
		if (columnOrder?.length) {
			const pinned = new Set(columnOrder);
			orderedKeys = [
				...columnOrder.filter((k) => allKeys.includes(k)),
				...allKeys.filter((k) => !pinned.has(k)).sort(),
			];
		} else {
			orderedKeys = allKeys;
		}
		return orderedKeys.map((key) => ({
			accessorKey: key,
			header: key,
			cell: ({ getValue }) => {
				const value = getValue();
				const customRenderer = cellRenderers?.[key];
				if (customRenderer)
					return (
						<CustomRendererCell
							value={value}
							label={key}
							customRenderer={customRenderer}
							onShowFull={setFullValue}
						/>
					);
				return (
					<CopyCell
						value={value}
						label={key}
						truncate={truncate}
						onShowFull={setFullValue}
					/>
				);
			},
		}));
	}, [data, truncate, cellRenderers]);

	const columnIds = useMemo(
		() => new Set(columns.map((c) => c.accessorKey)),
		[columns]
	);

	const effectiveSorting = useMemo(
		() => sorting.filter((s) => columnIds.has(s.id)),
		[sorting, columnIds]
	);

	const table = useReactTable({
		data: data || [],
		columns,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		enableSortingRemoval: false,
		state: { sorting: effectiveSorting },
		onSortingChange: (updater) => {
			const newSorting =
				typeof updater === "function"
					? updater(effectiveSorting)
					: updater;
			setSorting(newSorting);
			if (tableUniqueID) {
				dispatch(
					setTableSort(
						tableUniqueID,
						newSorting.map((s) => ({ id: s.id, desc: s.desc }))
					)
				);
			}
		},
	});

	if (!data || data.length === 0) {
		return (
			<span style={{ color: "#aaa", fontSize: 12, fontStyle: "italic" }}>
				No records
			</span>
		);
	}

	return (
		<>
			<div style={{ overflowX: "auto" }}>
				<table
					style={{
						borderCollapse: "collapse",
						fontSize: 12,
						fontFamily: "monospace",
						width: "100%",
						border: CELL_BORDER,
					}}
				>
					<thead>
						{table.getHeaderGroups().map((hg) => (
							<tr key={hg.id}>
								{hg.headers.map((header) => (
									<th
										key={header.id}
										style={{
											padding: "4px 8px",
											textAlign: "left",
											fontWeight: 700,
											whiteSpace: "nowrap",
											color: "#444",
											backgroundColor: "#f5f5f5",
											border: CELL_BORDER,
											cursor: "pointer",
											userSelect: "none",
										}}
										onClick={header.column.getToggleSortingHandler()}
									>
										<span
											style={{
												display: "flex",
												alignItems: "center",
												gap: 4,
											}}
										>
											{flexRender(
												header.column.columnDef.header,
												header.getContext()
											)}
											<SortIcon
												sorted={header.column.getIsSorted()}
											/>
										</span>
									</th>
								))}
							</tr>
						))}
					</thead>
					<tbody>
						{table.getRowModel().rows.map((row, i) => (
							<tr
								key={row.id}
								style={{
									backgroundColor:
										i % 2 === 0 ? "#fff" : "#fafafa",
								}}
							>
								{row.getVisibleCells().map((cell) => (
									<td
										key={cell.id}
										style={{
											padding: "3px 8px",
											verticalAlign: "middle",
											border: CELL_BORDER,
										}}
									>
										{flexRender(
											cell.column.columnDef.cell,
											cell.getContext()
										)}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
			<FullValueModal
				fullValue={fullValue}
				onClose={() => setFullValue(null)}
				defaultTitle="Cell value"
			/>
		</>
	);
}

export default DevDataTable;
